import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';

import { ACCOUNT_TYPES } from '@/accounts/constants/constants.js';
import { isPlatformApp } from '@pendo/middleware';

/**
 * @param {Array} filters
 * @param {Object} store
 * @returns {Object}
 */
export function mapFilters (filters = [], store) {
    return filters.reduce((prev, current) => {
        if (current.type === 'timeSeries' && current.value?.config) {
            return {
                ...prev,
                timeSeries: current.value.config,
            };
        }

        if (current.type === 'accountTypes') {
            return {
                ...prev,
                accountTypes: current.value.map((type) => {
                    const accountType = ACCOUNT_TYPES[type]?.setting;

                    return {
                        label: type,
                        value: type,
                        values: JSON.parse(store.state.subscription.settings[accountType]?.value || '[]'),
                    };
                }),
            };
        }

        return {
            ...prev,
            [current.type]: current.value,
        };
    }, {});
}

export function unmapFilters (filters = {}) {
    return Object.keys(filters).map((filterKey) => ({ type: filterKey, value: filters[filterKey] }));
}

export function formatView (savedView, { emptyFilters, emptyTable, store }) {
    const formattedSavedView = {
        prompt: '',
        topics: [],
        ...savedView,
        filters: {},
        ...(!savedView.columns && { columns: emptyTable.columns }),
        ...(!savedView.sort && { sort: emptyTable.sort }),
        starred: store.getters['stars/isStarredById'](savedView.id),
        locked: savedView.name === 'All Feedback' && savedView.createdByUser.username === '--system-user--',
    };
    const formattedSavedViewFilters = mapFilters(savedView.filters, store);
    if (formattedSavedViewFilters.allTime === false && !formattedSavedViewFilters.timeSeries) {
        formattedSavedViewFilters.timeSeries = emptyFilters.timeSeries;
    }

    if (formattedSavedViewFilters.timeSeries) {
        formattedSavedViewFilters.timeSeries = (formattedSavedViewFilters.timeSeries.config
            ? convertConfigToTimeSeries(formattedSavedViewFilters.timeSeries.config)
            : convertConfigToTimeSeries(formattedSavedViewFilters.timeSeries)
        ).config;
    }
    formattedSavedView.filters = formattedSavedViewFilters;

    formattedSavedViewFilters.allTime ??= !formattedSavedViewFilters.timeSeries;

    return formattedSavedView;
}

export function hasAccountTypesFilter (savedView = {}) {
    return !!savedView.filters?.find((filter) => filter.type === 'accountTypes')?.value?.length;
}

/**
 * Prepare top-level updates for saved view from filters.
 * Handles special cases for appIds and productAreaIds.
 *
 * @param {Object} filters - The filters object containing appIds, groups, segmentId, etc.
 * @returns {Object} Object with appIds, productAreaIds, and segmentId ready for top-level savedView
 */
export function prepareTopLevelUpdates (filters) {
    const cleanedProductAreaIds = filters.groups
        ? filters.groups.filter((id) => id !== '')
        : [];

    const isPlatformAppId = filters.appIds?.length > 0 && isPlatformApp(filters.appIds[0]);

    return {
        appIds: !isPlatformAppId ? (filters.appIds || []) : [],
        productAreaIds: cleanedProductAreaIds,
        segmentId: filters.segmentId || '',
    };
}
